/**
 * Cohort Schemas
 *
 * Single source of truth for the cohort form shape used by both the
 * inline Create dialog (CreateCohortDialog in AddTalentsWizard) and
 * the Edit dialog (EditCohortDialog in features/business/cohorts).
 *
 * Pre-fix the two surfaces declared independent schemas — Create had
 * 7 fields, Edit had only `name`. Admins couldn't fix a typo'd start
 * date or update feedback cadence after onboarding. The unification
 * derives both schemas from `cohortBaseFieldsSchema` so adding a new
 * field is a one-line change that propagates to both surfaces.
 *
 * Backend mirror: `apps/platform-api/.../business.controller.ts`
 * (`createCohortSchema` for POST, `updateCohortSchema` for PATCH).
 */

import { z } from 'zod';

export const cohortStatusSchema = z.enum([
  'draft',
  'recruiting',
  'active',
  'completed',
  'cancelled',
]);

/**
 * Shared editable fields — every field that's mutable post-creation
 * on the backend's PATCH `/api/business/cohorts/:id` endpoint. The
 * Create form extends this with `programId`; the Edit form uses it
 * directly. Status edits live on the existing Pause/Resume + Archive
 * menu actions (which carry cascade-archive confirmations) so they
 * intentionally don't surface here.
 */
export const cohortBaseFieldsSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'business.cohorts.errors.nameRequired' })
    .max(200, { message: 'business.cohorts.errors.nameTooLong' }),
  description: z
    .string()
    .max(2000, { message: 'business.cohorts.errors.descriptionTooLong' })
    .optional(),
  startDate: z
    .string()
    .min(1, { message: 'business.addTalentsWizard.errors.startDateRequired' })
    .refine((s) => !Number.isNaN(Date.parse(s)), {
      message: 'business.addTalentsWizard.errors.startDateInvalid',
    }),
  endDate: z
    .string()
    .optional()
    .refine((s) => !s || !Number.isNaN(Date.parse(s)), {
      message: 'business.addTalentsWizard.errors.endDateInvalid',
    }),
  // No `.default()` and no transforms — keeps input/output symmetrical
  // so RHF's typed generic accepts the same shape for default values
  // and handleSubmit's payload. Default is supplied via useForm.
  feedbackCadence: z.enum(['bi-weekly', 'monthly', 'quarterly']).optional(),
  expectedTalentCount: z.number().int().nonnegative().max(100000).optional(),
});

/**
 * Create form — adds the program selector to the shared base.
 *
 * `programId` is intentionally optional — cohorts can exist standalone
 * since migration 0041. Empty string from the dropdown gets coerced to
 * undefined.
 */
export const cohortIntakeSchema = cohortBaseFieldsSchema.extend({
  programId: z
    .uuid()
    .optional()
    .or(z.literal('').transform(() => undefined)),
});

export type CohortIntakeInput = z.infer<typeof cohortIntakeSchema>;

/**
 * Edit form — uses the shared base verbatim. programId is NOT
 * editable (the backend PATCH endpoint doesn't accept it; cohort →
 * program reassignment is a separate product concern that would
 * require cascading updates to `journey_instances.program_id` per
 * migration 0061).
 */
export const cohortEditSchema = cohortBaseFieldsSchema;

export type CohortEditInput = z.infer<typeof cohortEditSchema>;

export const enrollTalentSchema = z.object({
  cohortId: z.uuid(),
  talentId: z.uuid(),
  notes: z.string().max(500).optional(),
});

export type EnrollTalentInput = z.infer<typeof enrollTalentSchema>;

/**
 * Program creation form used inline by the wizard. Backend mirror is
 * `createProgramSchema` in business.controller.ts.
 */
export const programIntakeSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'business.addTalentsWizard.errors.programNameRequired' })
    .max(200),
  description: z.string().max(2000).optional(),
});

export type ProgramIntakeInput = z.infer<typeof programIntakeSchema>;

/**
 * Inline "Edit program" form — the dialog mutates `name` +
 * `description`. Mirrors the backend `updateProgramSchema`. Kept here
 * (next to the other intake schemas) rather than in `program.ts`
 * because the existing exports in that file describe the full
 * lifecycle DTOs, and the Cohorts/Programs portal forms historically
 * share this neighborhood.
 */
export const programEditSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'business.programs.errors.nameRequired' })
    .max(200, { message: 'business.programs.errors.nameTooLong' }),
  description: z
    .string()
    .max(2000, { message: 'business.programs.errors.descriptionTooLong' })
    .optional(),
});

export type ProgramEditInput = z.infer<typeof programEditSchema>;
